import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { parseElectionDate } from '../../../../lib/date-utils';
import { ENDORSERS } from '../../config/endorsers';
import { useGuide } from '../../hooks/useGuide';

const CATEGORY_META = {
  federal: {
    label: 'Federal',
    description: 'President, U.S. Senate, U.S. House of Representatives',
  },
  state: {
    label: 'State',
    description: 'Governor, state legislature, statewide offices',
  },
  local: {
    label: 'County & Local',
    description: 'County officials, city council, school board',
  },
  judges: {
    label: 'Judges',
    description: 'Judicial retention votes — keeping or removing sitting judges',
  },
  measures: {
    label: 'Ballot Measures',
    description: 'Propositions and amendments that become law if passed',
  },
};

const LEAN_LABEL = { left: 'Progressive', right: 'Conservative', nonpartisan: 'Nonpartisan' };

export default function GuideBallotOverview() {
  const navigate = useNavigate();
  const { lookupResult } = useGuide();

  useEffect(() => {
    if (!lookupResult) navigate('/', { replace: true });
  }, [lookupResult, navigate]);

  if (!lookupResult) return null;

  const { ballot, election } = lookupResult;
  const categories = Object.entries(CATEGORY_META).filter(
    ([key]) => (ballot?.categoryCounts?.[key] ?? 0) > 0
  );

  return (
    <div>
      <p className="g-label">Step 2 — Your Ballot</p>
      <h1 className="g-h1">
        {ballot.totalCount} things
        <br />
        on your ballot.
      </h1>
      <p className="g-body" style={{ marginBottom: 8 }}>
        {election.name} ·{' '}
        {parseElectionDate(election.electionDate).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })}
      </p>

      <div className="g-card" style={{ marginBottom: 24 }}>
        {categories.map(([key, meta]) => (
          <div key={key} className="g-step-row">
            <div
              style={{
                minWidth: 36,
                height: 36,
                background: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: 'var(--g-radius)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 900,
                fontSize: 18,
                color: 'var(--g-orange)',
                flexShrink: 0,
              }}
            >
              {ballot.categoryCounts[key]}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{meta.label}</div>
              <div style={{ fontSize: 13, color: 'var(--g-muted)' }}>{meta.description}</div>
            </div>
          </div>
        ))}
      </div>

      <hr className="g-divider" />

      <p className="g-label">Want a head start?</p>
      <p className="g-body" style={{ fontSize: 14, marginBottom: 16 }}>
        These organizations publish local endorsements. Visit their sites to see their picks — the
        app doesn't host endorsements directly.
      </p>

      {ENDORSERS.map((org) => (
        <a
          key={org.name}
          href={org.url}
          target="_blank"
          rel="noopener noreferrer"
          className="g-card"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            textDecoration: 'none',
            color: 'inherit',
            marginBottom: 8,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{org.name}</div>
            <div style={{ fontSize: 13, color: 'var(--g-muted)' }}>
              {LEAN_LABEL[org.lean]} · {org.description}
            </div>
          </div>
          <span style={{ color: 'var(--g-orange)', fontSize: 20, flexShrink: 0, marginLeft: 12 }}>
            →
          </span>
        </a>
      ))}

      <div style={{ marginTop: 24 }}>
        <button className="g-btn-primary" onClick={() => navigate('/guide/ballot-checklist')}>
          Start my ballot →
        </button>
      </div>
    </div>
  );
}
